/**
 * Okta Token Manager
 * Manages access tokens from Okta for API authorization
 */

let oktaAuthInstance = null;

export const oktaTokenManager = {
  /**
   * Initialize the token manager with OktaAuth instance
   * This should be called once during app initialization
   */
  initialize(oktaAuth) {
    oktaAuthInstance = oktaAuth;
  },

  /**
   * Get a valid access token from Okta
   * Automatically handles token refresh if needed
   * @returns {Promise<string|null>} Access token or null if not authenticated
   */
  async getValidAccessToken() {
    if (!oktaAuthInstance) {
      console.error('OktaTokenManager not initialized. Call initialize() first.');
      return null;
    }

    try {
      const authState = oktaAuthInstance.authStateManager.getAuthState();
      
      if (!authState?.isAuthenticated) {
        console.warn('User is not authenticated');
        return null;
      }

      // Get access token from authState
      const accessToken = authState.accessToken;
      
      if (!accessToken) {
        console.warn('No access token available');
        return null;
      }

      // Check if token is expired or about to expire
      const tokenManager = oktaAuthInstance.tokenManager;
      const token = await tokenManager.get('accessToken');
      
      if (!token) {
        console.warn('Access token not found in token manager');
        // Try to renew token if user is still authenticated
        try {
          await oktaAuthInstance.tokenManager.renew('accessToken');
          const renewedToken = await tokenManager.get('accessToken');
          return renewedToken?.accessToken || null;
        } catch (renewError) {
          console.error('Failed to renew access token:', renewError);
          return null;
        }
      }

      return token.accessToken;
    } catch (error) {
      console.error('Error getting Okta access token:', error);
      return null;
    }
  },

  /**
   * Get ID token from Okta (contains user claims)
   * @returns {Promise<string|null>} ID token or null
   */
  async getIdToken() {
    if (!oktaAuthInstance) {
      console.error('OktaTokenManager not initialized');
      return null;
    }

    try {
      const tokenManager = oktaAuthInstance.tokenManager;
      const token = await tokenManager.get('idToken');
      return token?.idToken || null;
    } catch (error) {
      console.error('Error getting Okta ID token:', error);
      return null;
    }
  },

  /**
   * Clear all Okta tokens
   */
  async clearTokens() {
    if (!oktaAuthInstance) {
      return;
    }

    try {
      await oktaAuthInstance.tokenManager.clear();
    } catch (error) {
      console.error('Error clearing Okta tokens:', error);
    }
  },

  /**
   * Check if user is authenticated with valid tokens
   * @returns {Promise<boolean>}
   */
  async isAuthenticated() {
    if (!oktaAuthInstance) {
      return false;
    }

    try {
      const authState = oktaAuthInstance.authStateManager.getAuthState();
      return authState?.isAuthenticated || false;
    } catch (error) {
      console.error('Error checking authentication status:', error);
      return false;
    }
  }
};
